<template>
  <div class="ctp"
       :class="{ 'ctp--disabled': disabled, 'ctp--open': isOpen }">
    <button ref="trigger"
            type="button"
            cy="ctp-trigger"
            class="ctp__trigger"
            :disabled="disabled"
            @click="open">
      <span class="ctp__trigger-text"
            :class="{ 'ctp__trigger-text--placeholder': !displayValue }">
        {{ displayValue || placeholder }}
      </span>
    </button>

    <div v-if="isOpen"
         ref="popover"
         cy="ctp-popover"
         class="ctp__popover"
         :class="{ 'ctp__popover--up': openUpward, 'ctp__popover--positioned': isPositioned }">
      <div class="ctp__header">
        <button type="button"
                cy="ctp-digital-hour"
                class="ctp__digital"
                :class="{ 'ctp__digital--active': mode === 'hour' }"
                @click="setMode('hour')">
          {{ pad(draftHour) }}
        </button>
        <span class="ctp__separator">:</span>
        <button type="button"
                cy="ctp-digital-minute"
                class="ctp__digital"
                :class="{ 'ctp__digital--active': mode === 'minute' }"
                @click="setMode('minute')">
          {{ pad(draftMinute) }}
        </button>
      </div>

      <div class="ctp__face-wrap">
        <ClockFace :mode="mode"
                   :selected-hour="draftHour"
                   :selected-minute="draftMinute"
                   @select="handleSelect" />
      </div>

      <div class="ctp__actions">
        <button type="button"
                cy="ctp-cancel"
                class="ctp__btn ctp__btn--text"
                @click="cancel">
          ANULEAZA
        </button>
        <button type="button"
                cy="ctp-ok"
                class="ctp__btn ctp__btn--text"
                @click="commit">
          OK
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ClockFace from './ClockFace.vue'

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

export default {
  name: 'ClockTimePicker',
  components: { ClockFace },
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '--:--'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isOpen: false,
      openUpward: false,
      isPositioned: false,
      mode: 'hour',
      draftHour: 0,
      draftMinute: 0,
      hourTouched: false,
      minuteTouched: false
    }
  },
  mounted () {
    document.addEventListener('mousedown', this.onDocumentMousedown, true)
    document.addEventListener('keydown', this.onDocumentKeydown, true)
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.onDocumentMousedown, true)
    document.removeEventListener('keydown', this.onDocumentKeydown, true)
  },
  computed: {
    displayValue () {
      return TIME_RE.test(this.value) ? this.value : ''
    }
  },
  methods: {
    pad (n) {
      return String(n).padStart(2, '0')
    },
    open () {
      if (this.disabled) return
      this.parseValue()
      this.mode = 'hour'
      this.hourTouched = false
      this.minuteTouched = false
      this.openUpward = false
      this.isPositioned = false
      this.isOpen = true
      this.$nextTick(() => {
        this.positionPopover()
        this.isPositioned = true
      })
    },
    positionPopover () {
      const trigger = this.$refs.trigger
      const popover = this.$refs.popover
      if (!trigger || !popover) return
      const triggerRect = trigger.getBoundingClientRect()
      const popoverHeight = popover.offsetHeight
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - triggerRect.bottom
      const spaceAbove = triggerRect.top
      const gap = 6
      if (spaceBelow < popoverHeight + gap && spaceAbove > spaceBelow) {
        this.openUpward = true
      }
    },
    parseValue () {
      const m = TIME_RE.exec(this.value)
      if (m) {
        this.draftHour = parseInt(m[1], 10)
        this.draftMinute = parseInt(m[2], 10)
      } else {
        this.draftHour = 0
        this.draftMinute = 0
      }
    },
    setMode (mode) {
      this.mode = mode
    },
    handleSelect (n) {
      if (this.mode === 'hour') {
        this.draftHour = n
        this.hourTouched = true
        this.mode = 'minute'
      } else {
        this.draftMinute = n
        this.minuteTouched = true
        this.commit()
      }
    },
    commit () {
      const out = `${this.pad(this.draftHour)}:${this.pad(this.draftMinute)}`
      this.$emit('input', out)
      this.isOpen = false
    },
    cancel () {
      this.isOpen = false
    },
    closeWithIntent () {
      if (this.hourTouched && this.minuteTouched) {
        this.commit()
      } else {
        this.cancel()
      }
    },
    onDocumentMousedown (e) {
      if (!this.isOpen) return
      if (this.$el && this.$el.contains(e.target)) return
      this.closeWithIntent()
    },
    onDocumentKeydown (e) {
      if (!this.isOpen) return
      if (e.key !== 'Escape') return
      this.closeWithIntent()
    }
  }
}
</script>

<style lang="less">
@import "../../styles/var";

.ctp {
  position: relative;
  display: inline-block;
  width: 100%;

  &__trigger {
    width: 100%;
    height: 34px;
    padding: 0 10px;
    font-size: 1.5rem;
    text-align: left;
    border: 1px solid #C3C3C3;
    border-radius: 6px;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) {
      border-color: @blue;
    }

    &:focus {
      outline: none;
      border-color: @blue;
      box-shadow: 0 0 0 2px rgba(41, 171, 226, 0.15);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__trigger-text {
    color: @dark-gray;

    &--placeholder {
      color: #999;
    }
  }

  &__popover {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 1;
    width: 280px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
    overflow: hidden;
    visibility: hidden;

    &--positioned {
      visibility: visible;
    }

    &--up {
      top: auto;
      bottom: calc(100% + 6px);
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 18px 16px;
    background: @blue;
    color: white;
  }

  &__digital {
    background: transparent;
    color: rgba(255, 255, 255, 0.65);
    border: none;
    font-size: 3.6rem;
    font-weight: 300;
    line-height: 1;
    padding: 0 6px;
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
    }

    &--active {
      color: white;
    }
  }

  &__separator {
    font-size: 3.6rem;
    font-weight: 300;
    line-height: 1;
    color: rgba(255, 255, 255, 0.65);
  }

  &__face-wrap {
    display: flex;
    justify-content: center;
    padding: 18px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
    padding: 0 10px 10px;
  }

  &__btn {
    background: transparent;
    border: none;
    font-size: 1.3rem;
    font-weight: bold;
    padding: 8px 14px;
    border-radius: 6px;
    cursor: pointer;
    color: @blue;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(41, 171, 226, 0.1);
    }
  }
}
</style>
